import { Card, CardContent, Chip, Stack } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import {
  getCalendarUnitAccent,
  hexToRgba,
} from "../../utils/helperFunctions/calendarTimelineHelper";

type CalendarLegendProps = {
  units: UnitResponse[];
  t: (key: string, values?: Record<string, string | number>) => string;
};

const CalendarLegend = ({ units, t }: CalendarLegendProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        mb: { xs: 1.5, md: 2 },
        borderRadius: { xs: 3, md: 4 },
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1, md: 2 },
          "&:last-child": {
            pb: { xs: 1, md: 2 },
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: { xs: "nowrap", md: "wrap" },
            rowGap: 1,
            overflowX: { xs: "auto", md: "visible" },
            overflowY: "hidden",
            pb: { xs: 0.25, md: 0 },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <LegendChip
            label={t("calendar.allUnits")}
            color={AppColors.primary}
          />

          {units.slice(0, 5).map((unit, index) => (
            <LegendChip
              key={unit.id}
              label={unit.name}
              color={getCalendarUnitAccent(unit, index)}
            />
          ))}

          <LegendChip label={t("calendar.checkInAfter")} color="#10b981" soft />
          <LegendChip
            label={t("calendar.checkOutBefore")}
            color="#64748b"
            soft
          />
          <LegendChip label={t("calendar.cleaning")} color="#e11d48" soft />
          <LegendChip label={t("calendar.private")} color="#64748b" soft />
          <LegendChip
            label={t("bookingSources.booking")}
            color="#2563eb"
            soft
          />
          <LegendChip label={t("bookingSources.airbnb")} color="#e11d48" soft />
          <LegendChip label={t("calendar.closed")} color="#f97316" soft />
        </Stack>
      </CardContent>
    </Card>
  );
};

const LegendChip = ({
  label,
  color,
  soft = false,
}: {
  label: string;
  color: string;
  soft?: boolean;
}) => {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: { xs: 28, md: 32 },
        borderRadius: "999px",
        fontWeight: 900,
        fontSize: { xs: 11, md: 13 },
        flexShrink: 0,
        color,
        bgcolor: soft ? hexToRgba(color, 0.12) : AppColors.surface,
        border: `1px solid ${soft ? hexToRgba(color, 0.28) : color}`,
        "& .MuiChip-label": {
          px: { xs: 1, md: 1.3 },
        },
        "&:before": {
          content: '""',
          display: "inline-block",
          width: { xs: 7, md: 8 },
          height: { xs: 7, md: 8 },
          borderRadius: "999px",
          bgcolor: color,
          ml: { xs: 1, md: 1.2 },
        },
      }}
    />
  );
};

export default CalendarLegend;
